import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Tabs,
  Tab,
  Button,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Paper,
} from '@mui/material';
import {
  Security as SecurityIcon,
  PrivacyTip as PrivacyTipIcon,
  BugReport as BugReportIcon,
  Description as DescriptionIcon,
  Settings as SettingsIcon,
  Info as InfoIcon,
  Shield as ShieldIcon,
  Lock as LockIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`system-tabpanel-${index}`}
      aria-labelledby={`system-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const SystemSettings: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h1" sx={{ mb: 2 }}>
          Системные настройки
        </Typography>
        <Typography variant="body1" sx={{ color: '#718096', maxWidth: 800 }}>
          Управление данными, безопасностью, конфиденциальностью и тестированием приложения.
        </Typography>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="О данных" icon={<InfoIcon />} iconPosition="start" />
          <Tab label="Безопасность" icon={<ShieldIcon />} iconPosition="start" />
          <Tab label="Конфиденциальность" icon={<LockIcon />} iconPosition="start" />
          <Tab label="Тестирование" icon={<AssessmentIcon />} iconPosition="start" />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h5" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <InfoIcon color="primary" />
              Информация о данных
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              Приложение использует актуальные медицинские данные из официальных источников.
            </Typography>
            
            <List>
              <ListItem>
                <ListItemIcon>
                  <DescriptionIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="Источники данных"
                  secondary="Официальные реестры Минздрава России, ВОЗ, CDC"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <SettingsIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="Обновление данных"
                  secondary="Автоматическое обновление при изменении источников"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <SecurityIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="Валидация данных"
                  secondary="Проверка корректности и актуальности медицинской информации"
                />
              </ListItem>
            </List>
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h5" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <ShieldIcon color="primary" />
              Безопасность и соответствие
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              Система безопасности обеспечивает защиту медицинских данных и соответствие требованиям.
            </Typography>
            
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2, mb: 3 }}>
              <Paper sx={{ p: 2, backgroundColor: '#F8F9FA' }}>
                <Typography variant="h6" sx={{ mb: 1, color: '#2D3748' }}>
                  Соответствие требованиям
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip label="152-ФЗ" color="primary" size="small" />
                  <Chip label="GDPR" color="primary" size="small" />
                  <Chip label="Медицинская лицензия" color="primary" size="small" />
                </Box>
              </Paper>
              
              <Paper sx={{ p: 2, backgroundColor: '#F8F9FA' }}>
                <Typography variant="h6" sx={{ mb: 1, color: '#2D3748' }}>
                  Защита данных
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip label="Шифрование" color="success" size="small" />
                  <Chip label="Резервное копирование" color="success" size="small" />
                  <Chip label="Аудит" color="success" size="small" />
                </Box>
              </Paper>
            </Box>

            <Alert severity="info" sx={{ mb: 2 }}>
              Все медицинские данные обрабатываются в соответствии с требованиями российского законодательства.
            </Alert>
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h5" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <LockIcon color="primary" />
              Конфиденциальность
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              Политика конфиденциальности и обработки персональных данных.
            </Typography>
            
            <List>
              <ListItem>
                <ListItemIcon>
                  <PrivacyTipIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="Сбор данных"
                  secondary="Минимально необходимый объем данных для функционирования приложения"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <SecurityIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="Хранение данных"
                  secondary="Локальное хранение на устройстве пользователя"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <SettingsIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="Управление данными"
                  secondary="Пользователь может удалить все данные в любой момент"
                />
              </ListItem>
            </List>

            <Divider sx={{ my: 2 }} />

            <Typography variant="h6" sx={{ mb: 2 }}>
              Ваши права
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip label="Доступ к данным" variant="outlined" />
              <Chip label="Исправление данных" variant="outlined" />
              <Chip label="Удаление данных" variant="outlined" />
              <Chip label="Ограничение обработки" variant="outlined" />
            </Box>
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h5" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <AssessmentIcon color="primary" />
              Тестирование системы
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              Инструменты для тестирования функциональности и производительности приложения.
            </Typography>
            
            <Alert severity="warning" sx={{ mb: 3 }}>
              Раздел предназначен для разработчиков и технических специалистов.
            </Alert>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2 }}>
              <Paper sx={{ p: 2, backgroundColor: '#F8F9FA' }}>
                <Typography variant="h6" sx={{ mb: 1, color: '#2D3748' }}>
                  Тесты функциональности
                </Typography>
                <Typography variant="body2" sx={{ mb: 2, color: '#718096' }}>
                  Проверка работы всех компонентов и функций
                </Typography>
                <Button variant="outlined" size="small">
                  Запустить тесты
                </Button>
              </Paper>
              
              <Paper sx={{ p: 2, backgroundColor: '#F8F9FA' }}>
                <Typography variant="h6" sx={{ mb: 1, color: '#2D3748' }}>
                  Тесты производительности
                </Typography>
                <Typography variant="body2" sx={{ mb: 2, color: '#718096' }}>
                  Мониторинг скорости работы и использования памяти
                </Typography>
                <Button variant="outlined" size="small">
                  Мониторинг
                </Button>
              </Paper>
            </Box>
          </CardContent>
        </Card>
      </TabPanel>
    </Box>
  );
};

export default SystemSettings;
